/**
 * The five daily jobs of piece 9 and their hours on the job clock (decision
 * 0346). 🔒 Each job registers under its own hour key, a SettingValue row seeded
 * once and edited on the Scheduled jobs card of Settings › Security; a job's
 * last success is its own run report, the job.ran audit row carrying the job's
 * name as correlationId. 🔨 Every job is idempotent, and a second run on one
 * day reports zero due.
 */
import { t, type StringKey } from '../strings';
import { RUN_LOW_STOCK_DEFAULT_HOUR, RUN_LOW_STOCK_HOUR_KEY, RUN_LOW_STOCK_JOB } from '../reports/lowStock';
import { CHECK_PM_SCHEDULES_DEFAULT_HOUR, CHECK_PM_SCHEDULES_HOUR_KEY, CHECK_PM_SCHEDULES_JOB } from '../assets/pmSchedule';
import { PURGE_PLANT_FEED_DEFAULT_HOUR, PURGE_PLANT_FEED_HOUR_KEY, PURGE_PLANT_FEED_JOB } from '../assets/plantFeed';
import { GENERATE_RECURRING_DRAFTS_DEFAULT_HOUR, GENERATE_RECURRING_DRAFTS_HOUR_KEY, GENERATE_RECURRING_DRAFTS_JOB } from '../documents/recurring';
import { GENERATE_SCHEDULED_INVOICES_DEFAULT_HOUR, GENERATE_SCHEDULED_INVOICES_HOUR_KEY, GENERATE_SCHEDULED_INVOICES_JOB } from '../documents/agreements';
import { SEND_VISIT_REMINDERS_JOB, VISIT_REMINDER_DEFAULT_HOUR, VISIT_REMINDER_HOUR_KEY } from '../documents/jobMessages';
import { CREW_RECHECK_DEFAULT_HOUR, CREW_RECHECK_HOUR_KEY, RECHECK_VISIT_CREWS_JOB } from '../documents/crewRecheck';
import { EXPIRE_COMPLETIONS_DEFAULT_HOUR, EXPIRE_COMPLETIONS_HOUR_KEY, EXPIRE_COMPLETIONS_JOB } from '../knowledge/model';
import { RECORD_CHECKS_SWEEP_DEFAULT_HOUR, RECORD_CHECKS_SWEEP_HOUR_KEY, RECORD_CHECKS_SWEEP_JOB } from '../records/recordSuggestions';
import { PURGE_OPEN_TRACKING_DEFAULT_HOUR, PURGE_OPEN_TRACKING_HOUR_KEY, PURGE_OPEN_TRACKING_JOB } from '../mail/settings';
import { SWEEP_UNANSWERED_TEXTS_DEFAULT_HOUR, SWEEP_UNANSWERED_TEXTS_HOUR_KEY, SWEEP_UNANSWERED_TEXTS_JOB } from '../identity/messaging';
import { PURGE_OUTSIDE_CALL_LOG_DEFAULT_HOUR, PURGE_OUTSIDE_CALL_LOG_HOUR_KEY, PURGE_OUTSIDE_CALL_LOG_JOB } from '../import/outsideCalls';

export const AGE_AUDIT_READS_JOB = 'ageAuditReads';
export const PURGE_QUARANTINE_JOB = 'purgeQuarantine';
export const REVIEW_NDA_DEMANDS_JOB = 'reviewNdaDemands';
export const RUN_REMINDERS_JOB = 'runReminders';
export const CLOSE_OPEN_CHECK_INS_JOB = 'closeOpenCheckIns';

export const AGE_AUDIT_READS_HOUR_KEY = 'audit.ageReadsRunHour';
export const PURGE_QUARANTINE_HOUR_KEY = 'files.purgeQuarantineRunHour';
export const REVIEW_NDA_DEMANDS_HOUR_KEY = 'nda.reviewDemandsRunHour';
export const RUN_REMINDERS_HOUR_KEY = 'reminders.runHour';
export const CLOSE_OPEN_CHECK_INS_HOUR_KEY = 'checkin.closeRunHour';

export interface ScheduledJobSpec {
  name: string;
  hourKey: string;
  defaultHour: number;
}

/** The five, in the order the Scheduled jobs card lists them; the check-in close runs at the first tick after midnight. */
export const PIECE_9_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [
  { name: CLOSE_OPEN_CHECK_INS_JOB, hourKey: CLOSE_OPEN_CHECK_INS_HOUR_KEY, defaultHour: 0 },
  { name: AGE_AUDIT_READS_JOB, hourKey: AGE_AUDIT_READS_HOUR_KEY, defaultHour: 1 },
  { name: PURGE_QUARANTINE_JOB, hourKey: PURGE_QUARANTINE_HOUR_KEY, defaultHour: 4 },
  { name: REVIEW_NDA_DEMANDS_JOB, hourKey: REVIEW_NDA_DEMANDS_HOUR_KEY, defaultHour: 5 },
  { name: RUN_REMINDERS_JOB, hourKey: RUN_REMINDERS_HOUR_KEY, defaultHour: 6 },
];

/** Piece 23's daily jobs: the finance charge and the ledger's two self-checks, on the same clock under their own hour keys (built 2026-09-15). */
export const RUN_FINANCE_CHARGES_JOB = 'runFinanceCharges';
export const RUN_FINANCE_CHARGES_HOUR_KEY = 'ar.financeChargeRunHour';
/** Decision 0407: the sub-ledger tie-outs, and the nightly re-derivation of the last N days' entries. */
export const RUN_LEDGER_TIE_OUTS_JOB = 'runLedgerTieOuts';
export const RUN_LEDGER_TIE_OUTS_HOUR_KEY = 'ledger.tieOutRunHour';
export const RUN_POSTING_REDERIVATION_JOB = 'runPostingRederivation';
export const RUN_POSTING_REDERIVATION_HOUR_KEY = 'ledger.rederivationRunHour';
/** How far back the re-derivation walks, in days; a `SettingValue` row seeded at 30. */
export const REDERIVATION_DAYS_KEY = 'ledger.rederivationDays';
export const REDERIVATION_DAYS_DEFAULT = 30;
export const PIECE_23_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [
  { name: RUN_LEDGER_TIE_OUTS_JOB, hourKey: RUN_LEDGER_TIE_OUTS_HOUR_KEY, defaultHour: 3 },
  { name: RUN_POSTING_REDERIVATION_JOB, hourKey: RUN_POSTING_REDERIVATION_HOUR_KEY, defaultHour: 4 },
  { name: RUN_FINANCE_CHARGES_JOB, hourKey: RUN_FINANCE_CHARGES_HOUR_KEY, defaultHour: 7 },
];

/** Piece 23's own non-hour settings the install seed writes once: how far back the re-derivation looks. */
export const PIECE_23_CHECK_SETTINGS: readonly { key: string; value: number }[] = [{ key: REDERIVATION_DAYS_KEY, value: REDERIVATION_DAYS_DEFAULT }];

/** The Organization-scoped rows the install seed writes once and never overwrites. */
export const PIECE_9_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_9_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/** Piece 23's hour rows, seeded at 3, 4 and 7 beside piece 9's five. */
export const PIECE_23_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_23_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/** Piece 25's daily jobs: the return generator and the nexus re-windowing, each under its own hour key (built 2026-09-15). */
export const GENERATE_SALES_TAX_FILINGS_JOB = 'generateSalesTaxFilings';
export const GENERATE_SALES_TAX_FILINGS_HOUR_KEY = 'tax.generateFilingsRunHour';
export const REWINDOW_NEXUS_COUNTERS_JOB = 'rewindowNexusCounters';
export const REWINDOW_NEXUS_COUNTERS_HOUR_KEY = 'tax.rewindowNexusRunHour';
export const PIECE_25_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [
  { name: REWINDOW_NEXUS_COUNTERS_JOB, hourKey: REWINDOW_NEXUS_COUNTERS_HOUR_KEY, defaultHour: 2 },
  { name: GENERATE_SALES_TAX_FILINGS_JOB, hourKey: GENERATE_SALES_TAX_FILINGS_HOUR_KEY, defaultHour: 5 },
];

/** Piece 25's hour rows, seeded at 2 and 5 beside the eight already on the clock. */
export const PIECE_25_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_25_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/**
 * Piece 24's daily job: every connected bank and card feed pulled on the clock
 * (decision 0433, built 2026-09-16). 🔨 Card accounts run in the same job,
 * because a second card importer is the second mechanism piece 24 refuses
 * everywhere else. 🔨 One account's failure is recorded and the walk
 * continues, so a provider that is down costs its own account and no other.
 */
export const PULL_BANK_FEEDS_JOB = 'pullBankFeeds';
export const PULL_BANK_FEEDS_HOUR_KEY = 'banking.pullFeedsRunHour';
export const PIECE_24_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [
  { name: PULL_BANK_FEEDS_JOB, hourKey: PULL_BANK_FEEDS_HOUR_KEY, defaultHour: 8 },
  // 🔒 Decisions 11020 and 11520: the recurring Draft Bills and Draft journals, at the hour `accounting.recurringRunHour` names, seeded at 5.
  { name: GENERATE_RECURRING_DRAFTS_JOB, hourKey: GENERATE_RECURRING_DRAFTS_HOUR_KEY, defaultHour: GENERATE_RECURRING_DRAFTS_DEFAULT_HOUR },
];

/** Piece 24's hour row, seeded at 8 beside the ten already on the clock. */
export const PIECE_24_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_24_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/**
 * Piece 26's daily job: every shelf judged against its own threshold, raising
 * one to-do per shelf that has crossed it (decision 0508). 🔨 It creates no
 * Purchase Order, ever: what to buy and from whom is a person's decision, and a
 * job that ordered on its own would order against a threshold nobody re-reads.
 */
export const PIECE_26_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [{ name: RUN_LOW_STOCK_JOB, hourKey: RUN_LOW_STOCK_HOUR_KEY, defaultHour: RUN_LOW_STOCK_DEFAULT_HOUR }];

/** Piece 26's hour row, seeded at 6 beside the eleven already on the clock. */
export const PIECE_26_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_26_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/**
 * Piece 27's daily job: the annual 1099 threshold reminder (decision 0521).
 * 🔨 It is not optional and no Settings switch turns it off — the hour is the
 * only knob. On its first run in a calendar year it raises one item; every
 * later run that year reports zero due, like every other job here.
 */
export const RAISE_FORM_1099_REMINDER_JOB = 'raiseForm1099Reminder';
export const RAISE_FORM_1099_REMINDER_HOUR_KEY = 'tax.form1099ReminderRunHour';
export const PIECE_27_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [{ name: RAISE_FORM_1099_REMINDER_JOB, hourKey: RAISE_FORM_1099_REMINDER_HOUR_KEY, defaultHour: 6 }];

/** Piece 27's hour row, seeded at 6 beside the twelve already on the clock. */
export const PIECE_27_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_27_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/**
 * Piece 28's daily job: every active maintenance schedule judged against the
 * period it last generated, one Draft Work Order per due period (decision
 * 0720). 🔨 It rides this clock and no timer of its own, as the plan requires.
 */
export const PIECE_28_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [
  { name: CHECK_PM_SCHEDULES_JOB, hourKey: CHECK_PM_SCHEDULES_HOUR_KEY, defaultHour: CHECK_PM_SCHEDULES_DEFAULT_HOUR },
  // 🔒 Decision 12504: the plant feed's readings, fault messages and cleared faults go after assets.feedKeepDays, at the hour its key names, seeded at 4.
  { name: PURGE_PLANT_FEED_JOB, hourKey: PURGE_PLANT_FEED_HOUR_KEY, defaultHour: PURGE_PLANT_FEED_DEFAULT_HOUR },
];

/** Piece 28's hour row, seeded at 5 beside the thirteen already on the clock. */
export const PIECE_28_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_28_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/** Piece 29, decision 0733: the recurring invoice generator, on the same clock under its own hour key, seeded at 6. */
export const PIECE_29_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [
  { name: GENERATE_SCHEDULED_INVOICES_JOB, hourKey: GENERATE_SCHEDULED_INVOICES_HOUR_KEY, defaultHour: GENERATE_SCHEDULED_INVOICES_DEFAULT_HOUR },
  // 🔒 T1167: the customer's Reminder the day before a booked visit, at the hour `workorder.visitReminderHour` names, seeded at 16.
  { name: SEND_VISIT_REMINDERS_JOB, hourKey: VISIT_REMINDER_HOUR_KEY, defaultHour: VISIT_REMINDER_DEFAULT_HOUR },
  // 🔒 Decision 4340: each Dispatched visit's crew rechecked on its day, at the hour `workorder.crewRecheckHour` names, seeded at 6.
  { name: RECHECK_VISIT_CREWS_JOB, hourKey: CREW_RECHECK_HOUR_KEY, defaultHour: CREW_RECHECK_DEFAULT_HOUR },
];
export const PIECE_29_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_29_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/** Piece 30, decision 0754: a lapsed completion moves to Expired, on the same clock under its own hour key, seeded at 2. */
export const PIECE_30_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [{ name: EXPIRE_COMPLETIONS_JOB, hourKey: EXPIRE_COMPLETIONS_HOUR_KEY, defaultHour: EXPIRE_COMPLETIONS_DEFAULT_HOUR }];
export const PIECE_30_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_30_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/**
 * 🔒 Piece 31, decision 3801: the daily pull of what changed in Zoho Books, on
 * the same clock under its own hour key, seeded at 2 in the organization's
 * zone. It reads nothing while sync mode's switch is off or past its end date.
 */
export const PULL_ZOHO_BOOKS_CHANGES_JOB = 'pullZohoBooksChanges';
export const PULL_ZOHO_BOOKS_CHANGES_HOUR_KEY = 'zohoBooks.syncRunHour';
export const PULL_ZOHO_BOOKS_CHANGES_DEFAULT_HOUR = 2;
export const PIECE_31_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [
  { name: PULL_ZOHO_BOOKS_CHANGES_JOB, hourKey: PULL_ZOHO_BOOKS_CHANGES_HOUR_KEY, defaultHour: PULL_ZOHO_BOOKS_CHANGES_DEFAULT_HOUR },
  // 🔒 Decision 5720: the outside-call log's rows go seven days after their call, under their own hour key, seeded at 3.
  { name: PURGE_OUTSIDE_CALL_LOG_JOB, hourKey: PURGE_OUTSIDE_CALL_LOG_HOUR_KEY, defaultHour: PURGE_OUTSIDE_CALL_LOG_DEFAULT_HOUR },
];
export const PIECE_31_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_31_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/** Piece 32, decision 0809: the open-tracking detail purge, on the same clock under its own hour key, seeded at 3. */
export const PIECE_32_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [{ name: PURGE_OPEN_TRACKING_JOB, hourKey: PURGE_OPEN_TRACKING_HOUR_KEY, defaultHour: PURGE_OPEN_TRACKING_DEFAULT_HOUR }];
export const PIECE_32_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_32_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/** Piece 13, decisions 5382 and 5631: the nightly sweep of bare names, under recordChecks.sweepRunHour, seeded at 3. */
export const PIECE_13_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [{ name: RECORD_CHECKS_SWEEP_JOB, hourKey: RECORD_CHECKS_SWEEP_HOUR_KEY, defaultHour: RECORD_CHECKS_SWEEP_DEFAULT_HOUR }];
export const PIECE_13_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_13_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/** Piece 21, decision 10710: a text with no receipt past the window is aged to Unknown, on the same clock under its own hour key, seeded at 4. */
export const PIECE_21_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [{ name: SWEEP_UNANSWERED_TEXTS_JOB, hourKey: SWEEP_UNANSWERED_TEXTS_HOUR_KEY, defaultHour: SWEEP_UNANSWERED_TEXTS_DEFAULT_HOUR }];
export const PIECE_21_JOB_SETTINGS: readonly { key: string; value: number }[] = PIECE_21_SCHEDULED_JOBS.map((job) => ({ key: job.hourKey, value: job.defaultHour }));

/**
 * Every job on the clock, whichever piece built it; the Scheduled jobs card
 * and the seed both read this. 🔨 A job joins this list in the commit that
 * gives it a runner, never before: the clock refuses a name it cannot run
 * (piece 25's two join it with the nexus and filing services).
 */
export const ALL_SCHEDULED_JOBS: readonly ScheduledJobSpec[] = [...PIECE_9_SCHEDULED_JOBS, ...PIECE_23_SCHEDULED_JOBS, ...PIECE_24_SCHEDULED_JOBS, ...PIECE_25_SCHEDULED_JOBS, ...PIECE_26_SCHEDULED_JOBS, ...PIECE_27_SCHEDULED_JOBS, ...PIECE_28_SCHEDULED_JOBS, ...PIECE_29_SCHEDULED_JOBS, ...PIECE_30_SCHEDULED_JOBS, ...PIECE_31_SCHEDULED_JOBS, ...PIECE_32_SCHEDULED_JOBS, ...PIECE_13_SCHEDULED_JOBS, ...PIECE_21_SCHEDULED_JOBS];

/**
 * 🔨 A JOB'S `name` IS A KEY AND NEVER A LABEL (decision 0472). The runner map,
 * the route and the `job.ran` audit row's `correlationId` all read it, so it
 * stays as it is spelled; what a person reads is the label below and the line
 * under it saying what the job does. Every job on the clock has both, and the
 * suite holds the registry to `ALL_SCHEDULED_JOBS`.
 */
export const SCHEDULED_JOB_STRINGS: Readonly<Record<string, { label: StringKey; purpose: StringKey }>> = {
  [CLOSE_OPEN_CHECK_INS_JOB]: { label: 'jobs.closeOpenCheckIns.label', purpose: 'jobs.closeOpenCheckIns.purpose' },
  [AGE_AUDIT_READS_JOB]: { label: 'jobs.ageAuditReads.label', purpose: 'jobs.ageAuditReads.purpose' },
  [PURGE_QUARANTINE_JOB]: { label: 'jobs.purgeQuarantine.label', purpose: 'jobs.purgeQuarantine.purpose' },
  [REVIEW_NDA_DEMANDS_JOB]: { label: 'jobs.reviewNdaDemands.label', purpose: 'jobs.reviewNdaDemands.purpose' },
  [RUN_REMINDERS_JOB]: { label: 'jobs.runReminders.label', purpose: 'jobs.runReminders.purpose' },
  [RUN_LEDGER_TIE_OUTS_JOB]: { label: 'jobs.runLedgerTieOuts.label', purpose: 'jobs.runLedgerTieOuts.purpose' },
  [RUN_POSTING_REDERIVATION_JOB]: { label: 'jobs.runPostingRederivation.label', purpose: 'jobs.runPostingRederivation.purpose' },
  [RUN_FINANCE_CHARGES_JOB]: { label: 'jobs.runFinanceCharges.label', purpose: 'jobs.runFinanceCharges.purpose' },
  [REWINDOW_NEXUS_COUNTERS_JOB]: { label: 'jobs.rewindowNexusCounters.label', purpose: 'jobs.rewindowNexusCounters.purpose' },
  [GENERATE_SALES_TAX_FILINGS_JOB]: { label: 'jobs.generateSalesTaxFilings.label', purpose: 'jobs.generateSalesTaxFilings.purpose' },
  [GENERATE_RECURRING_DRAFTS_JOB]: { label: 'jobs.generateRecurringDrafts.label', purpose: 'jobs.generateRecurringDrafts.purpose' },
  [PULL_BANK_FEEDS_JOB]: { label: 'jobs.pullBankFeeds.label', purpose: 'jobs.pullBankFeeds.purpose' },
  [RUN_LOW_STOCK_JOB]: { label: 'jobs.runLowStock.label', purpose: 'jobs.runLowStock.purpose' },
  [RAISE_FORM_1099_REMINDER_JOB]: { label: 'jobs.raiseForm1099Reminder.label', purpose: 'jobs.raiseForm1099Reminder.purpose' },
  [CHECK_PM_SCHEDULES_JOB]: { label: 'jobs.checkPMSchedules.label', purpose: 'jobs.checkPMSchedules.purpose' },
  [PURGE_PLANT_FEED_JOB]: { label: 'jobs.purgePlantFeed.label', purpose: 'jobs.purgePlantFeed.purpose' },
  [GENERATE_SCHEDULED_INVOICES_JOB]: { label: 'jobs.generateScheduledInvoices.label', purpose: 'jobs.generateScheduledInvoices.purpose' },
  [SEND_VISIT_REMINDERS_JOB]: { label: 'jobs.sendVisitReminders.label', purpose: 'jobs.sendVisitReminders.purpose' },
  [RECHECK_VISIT_CREWS_JOB]: { label: 'jobs.recheckVisitCrews.label', purpose: 'jobs.recheckVisitCrews.purpose' },
  [EXPIRE_COMPLETIONS_JOB]: { label: 'jobs.expireRequirementCompletions.label', purpose: 'jobs.expireRequirementCompletions.purpose' },
  [PULL_ZOHO_BOOKS_CHANGES_JOB]: { label: 'jobs.pullZohoBooksChanges.label', purpose: 'jobs.pullZohoBooksChanges.purpose' },
  [PURGE_OPEN_TRACKING_JOB]: { label: 'jobs.purgeOpenTrackingDetail.label', purpose: 'jobs.purgeOpenTrackingDetail.purpose' },
  [PURGE_OUTSIDE_CALL_LOG_JOB]: { label: 'jobs.purgeOutsideCallLog.label', purpose: 'jobs.purgeOutsideCallLog.purpose' },
  [RECORD_CHECKS_SWEEP_JOB]: { label: 'jobs.recordChecksSweep.label', purpose: 'jobs.recordChecksSweep.purpose' },
  [SWEEP_UNANSWERED_TEXTS_JOB]: { label: 'jobs.sweepUnansweredTexts.label', purpose: 'jobs.sweepUnansweredTexts.purpose' },
};

/**
 * What the card calls this job. 🔨 A job with no entry falls back to its own
 * name rather than drawing nothing, so a job added without a label is visibly
 * wrong on the screen instead of invisibly missing.
 */
export function jobLabel(name: string): string {
  const entry = SCHEDULED_JOB_STRINGS[name];
  return entry ? t(entry.label) : name;
}

/** The one line under the label saying what the job does; empty where none is registered. */
export function jobPurpose(name: string): string {
  const entry = SCHEDULED_JOB_STRINGS[name];
  return entry ? t(entry.purpose) : '';
}

/** A run hour is a whole hour of the day in the Organization's zone. */
export function runHourProblem(value: unknown): string | null {
  const hour = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return 'a run hour is a whole number from 0 to 23';
  return null;
}

/** The one application log line a clock run writes beside its run report. */
export function jobLogLine(name: string, ranAt: Date, rowsDue: number, rowsDone: number): string {
  return `scheduled job ${name} ran at ${ranAt.toISOString()}: ${rowsDue} due, ${rowsDone} done`;
}
